/**
 * Canonical API paths aligned with backend OpenAPI (`/api-doc/openapi.json`).
 * Override school paths per deployment via REACT_APP_* env vars at build time.
 */

function envPath(envKey: string, fallback: string): string {
  const value = process.env[envKey]?.trim();
  return value || fallback;
}

/** Tenant school/org profile paths (both `/api/school` and `/school` exist on backend). */
export const SCHOOL_API_PATHS = {
  profile: envPath('REACT_APP_SCHOOL_PROFILE_URL', '/api/school'),
  branches: envPath('REACT_APP_SCHOOL_BRANCHES_URL', '/api/school/branches'),
  /** POST requires `/academic-planning/academic-years`; GET also works on `/api/academic-years`. */
  academicYears: envPath('REACT_APP_ACADEMIC_YEARS_URL', '/academic-planning/academic-years'),
} as const;

export const AUTH_PATHS = {
  config: '/config',
  loginBasic: '/auth/login/basic',
  logout: '/auth/logout',
  sessionRotate: '/auth/session/rotate',
  passwordChange: '/auth/password/change',
  passwordResetRequest: '/auth/password-reset/request',
  passwordResetConfirm: '/auth/password-reset/confirm',
  zohoAuthorize: '/auth/oauth/zoho/authorize',
  zohoCallback: '/auth/oauth/zoho/callback',
  googleAuthorize: '/auth/oauth/google/authorize',
  googleCallback: '/auth/oauth/google/callback',
} as const;

export const TENANT_PATHS = {
  settings: '/tenants/me',
  subscriptionsModules: '/subscriptions/modules',
  resolve: '/tenants/resolve',
} as const;

export const SUPER_ADMIN_PATHS = {
  login: '/super-admin/login',
  logout: '/super-admin/logout',
  passwordChange: '/super-admin/password/change',
  tenants: '/super-admin/tenants',
  backfillSlugs: '/super-admin/tenants/backfill-slugs',
  billing: '/super-admin/billing',
  billingDunningConfig: '/super-admin/billing/dunning-config',
  billingDunningRun: '/super-admin/billing/dunning/run',
  feeTemplates: '/super-admin/fee-templates',
  feeTemplate: (templateId: string) =>
    `/super-admin/fee-templates/${encodeURIComponent(templateId)}`,
  expenseCategories: '/super-admin/expense-categories',
  expenseCategory: (categoryId: string) =>
    `/super-admin/expense-categories/${encodeURIComponent(categoryId)}`,
  loginVisits: '/super-admin/login-visits',
  tenantSlug: (tenantId: string | number) =>
    `/super-admin/tenants/${encodeURIComponent(String(tenantId))}/slug`,
  tenantProductOffering: (tenantId: string | number) =>
    `/super-admin/tenants/${encodeURIComponent(String(tenantId))}/product-offering`,
  tenantStudentResidenceMode: (tenantId: string | number) =>
    `/super-admin/tenants/${encodeURIComponent(String(tenantId))}/student-residence-mode`,
  tenantModules: (tenantId: string | number) =>
    `/super-admin/tenants/${encodeURIComponent(String(tenantId))}/modules`,
  tenantBilling: (tenantId: string | number) =>
    `/super-admin/tenants/${encodeURIComponent(String(tenantId))}/billing`,
  tenantBillingDunningExempt: (tenantId: string | number) =>
    `/super-admin/tenants/${encodeURIComponent(String(tenantId))}/billing/dunning-exempt`,
  tenantPayments: (tenantId: string | number) =>
    `/super-admin/tenants/${encodeURIComponent(String(tenantId))}/payments`,
  tenantOwner: (tenantId: string | number) =>
    `/super-admin/tenants/${encodeURIComponent(String(tenantId))}/owner`,
  tenantOwnerResetPassword: (tenantId: string | number) =>
    `/super-admin/tenants/${encodeURIComponent(String(tenantId))}/owner/reset-password`,
  tenantOwnerResendWelcome: (tenantId: string | number) =>
    `/super-admin/tenants/${encodeURIComponent(String(tenantId))}/owner/resend-welcome`,
  tenantOwnerTransfer: (tenantId: string | number) =>
    `/super-admin/tenants/${encodeURIComponent(String(tenantId))}/owner/transfer`,
  tenantOwnerDeactivate: (tenantId: string | number) =>
    `/super-admin/tenants/${encodeURIComponent(String(tenantId))}/owner/deactivate`,
  tenantImpersonate: (tenantId: string | number) =>
    `/super-admin/tenants/${encodeURIComponent(String(tenantId))}/impersonate`,
  tenantDashboardLayoutCatalog: (tenantId: string | number) =>
    `/super-admin/tenants/${encodeURIComponent(String(tenantId))}/dashboard-layout/catalog`,
  tenantDashboardLayout: (tenantId: string | number) =>
    `/super-admin/tenants/${encodeURIComponent(String(tenantId))}/dashboard-layout`,
  tenantAudits: (tenantId: string | number) =>
    `/super-admin/tenants/${encodeURIComponent(String(tenantId))}/audits`,
  tenantCommunicationConfig: (tenantId: string | number) =>
    `/super-admin/tenants/${encodeURIComponent(String(tenantId))}/communication-config`,
  tenantPaytmSettings: (tenantId: string | number) =>
    `/super-admin/tenants/${encodeURIComponent(String(tenantId))}/paytm-settings`,
  communicationUsage: '/super-admin/communication/usage',
  communicationUsageReport: '/super-admin/communication/usage/report',
} as const;

export const CAREER_PATHS = {
  publicMeta: '/public/careers/meta',
  publicJobs: '/public/careers/jobs',
  publicApply: '/public/careers/applications',
  adminConfig: '/super-admin/careers/config',
  adminJobs: '/super-admin/careers/jobs',
  adminJob: (id: string) => `/super-admin/careers/jobs/${encodeURIComponent(id)}`,
  adminApplications: '/super-admin/careers/applications',
  adminApplication: (id: string) => `/super-admin/careers/applications/${encodeURIComponent(id)}`,
  adminResume: (id: string) =>
    `/super-admin/careers/applications/${encodeURIComponent(id)}/resume`,
} as const;

/** Public login-page visit tracking (no auth). */
export const LOGIN_VISIT_PATHS = {
  record: '/public/login-visits',
  count: '/public/login-visits/count',
} as const;

/** Dashboard paths (tenant-scoped via x-tenant-id; used by admin + super-admin with tenant override). */
export const DASHBOARD_PATHS = {
  stats: '/dashboard/stats',
  activities: '/dashboard/activities',
  layout: '/dashboard/layout',
  layoutCatalog: '/dashboard/layout/catalog',
  layoutPreferences: '/dashboard/layout/preferences',
  detailPanels: '/dashboard/detail-panels',
  collectionDetails: '/dashboard/collection-details',
  punctualPayers: '/dashboard/punctual-payers',
  feeDefaulters: '/dashboard/fee-defaulters',
  visitorReport: '/dashboard/visitor-report',
  securityOverview: '/dashboard/security-overview',
  tenantLayoutCatalog: '/dashboard/tenant-layout/catalog',
  tenantLayout: '/dashboard/tenant-layout',
} as const;

export const MODULE_PATHS = {
  catalog: '/modules/catalog',
  sidebarOrder: '/modules/sidebar-order',
  myModuleAccess: '/users/me/module-access',
  myApiAccess: '/users/me/api-access',
} as const;

export const MEDIA_PATHS = {
  upload: '/media/upload',
  uploadDocument: '/media/upload-document',
  url: (mediaId: string) => `/media/${encodeURIComponent(mediaId)}/url`,
} as const;

export const OPENAPI_PATH = '/api-doc/openapi.json';
